import { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';

/* ---------------------------
   Types (GET /v1/fundamentals/comparison[/periods])
   --------------------------- */

/** Montants PUBLIÉS par l'émetteur (jamais le retraitement interne de valorisation). */
export interface ResultsPeriodValues {
  exercice: number;
  period_type: string;
  revenue: number | null;
  ebit: number | null;
  net_income: number | null;
}

export interface CompanyResultRow {
  symbole: string;
  nom_societe: string | null;
  secteur: string | null;
  target_period: ResultsPeriodValues;
  base_period: ResultsPeriodValues | null;
  revenue_var_abs: number | null;
  revenue_var_pct: number | null;
  ebit_var_abs: number | null;
  ebit_var_pct: number | null;
  net_income_var_abs: number | null;
  net_income_var_pct: number | null;
}

export interface CompanyResultsResponse {
  target_exercice: number;
  target_period_type: string;
  base_exercice: number;
  base_period_type: string;
  rows: CompanyResultRow[];
}

export interface ResultsPeriodOption {
  period_type: string;
  exercice: number;
  closing_date: string;
  published: number;
  total: number;
  coverage: number;
}

export interface ResultsSelection {
  period_type: string;
  exercice: number;
}

export interface ResultsPeriodsResponse {
  data: ResultsPeriodOption[];
  meta: { default: ResultsSelection | null; min_coverage: number };
}

/**
 * Page « Résultats des sociétés » : choix disponibles (2 derniers exercices publiés
 * par période, calculés côté serveur) puis résultats de la sélection.
 */
export function useCompanyResults(enabled: boolean) {
  const { token } = useAuth();
  const [periods, setPeriods] = useState<ResultsPeriodsResponse | null>(null);
  const [selection, setSelection] = useState<ResultsSelection | null>(null);
  const [results, setResults] = useState<CompanyResultsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // Seule la réponse de la DERNIÈRE sélection doit s'afficher (changements rapides).
  const requestId = useRef(0);

  useEffect(() => {
    if (!enabled || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/v1/fundamentals/comparison/periods`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (!res.ok) {
          setError(`Erreur ${res.status} lors du chargement des périodes disponibles.`);
          return;
        }
        const data: ResultsPeriodsResponse = await res.json();
        if (cancelled) return;
        setPeriods(data);
        if (data.meta.default) setSelection(data.meta.default);
        else setError('Aucun résultat publié disponible.');
      } catch (e) {
        if (cancelled) return;
        console.error('Error fetching results periods:', e);
        setError('Impossible de charger les périodes disponibles.');
      }
    })();
    return () => { cancelled = true; };
  }, [enabled, token]);

  const fetchResults = useCallback(async (sel: ResultsSelection) => {
    if (!token) return;
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    // Jamais afficher les chiffres d'une sélection précédente sous le libellé de la nouvelle.
    setResults(null);
    try {
      const res = await fetch(
        `${API_URL}/v1/fundamentals/comparison?period_type=${sel.period_type}&exercice=${sel.exercice}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (id !== requestId.current) return;
      if (res.ok) {
        const data: CompanyResultsResponse = await res.json();
        setResults(data);
      } else {
        const body = await res.json().catch(() => null);
        setError(body?.detail ?? `Erreur ${res.status}`);
      }
    } catch (e) {
      if (id !== requestId.current) return;
      console.error('Error fetching company results:', e);
      setError('Impossible de charger les résultats.');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (selection) fetchResults(selection);
  }, [selection, fetchResults]);

  return { periods, selection, setSelection, results, loading, error };
}
